<template>
  <Transition name="modal">
    <div v-if="isVisible" class="fixed inset-0 z-50 flex items-center justify-center">
      <!-- Overlay oscuro con blur -->
      <div class="absolute inset-0 bg-black/60 backdrop-blur-sm" @click="$emit('close')"></div>
      
      <!-- Modal -->
      <div class="relative bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4 transform transition-all">
        <!-- Animación del spinner -->
        <div class="flex flex-col items-center space-y-6">
          <!-- Spinner con gradiente animado -->
          <div class="relative">
            <div class="w-24 h-24 rounded-full border-4 border-gray-200"></div>
            <div class="absolute top-0 left-0 w-24 h-24 rounded-full border-4 border-t-blue-500 border-r-blue-400 border-b-transparent border-l-transparent animate-spin"></div>
            
            <!-- Icono central -->
            <div class="absolute inset-0 flex items-center justify-center">
              <svg class="w-10 h-10 text-blue-500 animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
              </svg>
            </div>
          </div>

          <!-- Título -->
          <div class="text-center">
            <h3 class="text-2xl font-bold text-gray-900 mb-2">
              {{ title }}
            </h3>
            <p class="text-gray-600">
              {{ message }}
            </p>
          </div>

          <!-- Barra de progreso animada -->
          <div class="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
            <div class="h-full bg-gradient-to-r from-blue-500 via-blue-600 to-blue-500 rounded-full animate-progress"></div>
          </div>

          <!-- Mensaje del agente actual -->
          <div v-if="currentAgent" class="w-full bg-blue-50 rounded-lg p-4 border border-blue-200">
            <div class="flex items-center space-x-3">
              <div class="flex-shrink-0">
                <div class="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                  <span class="text-white text-sm font-bold">{{ currentAgentNumber }}</span>
                </div>
              </div>
              <div class="flex-1">
                <p class="text-sm font-medium text-blue-900">{{ currentAgent }}</p>
                <p class="text-xs text-blue-600 mt-1">{{ agentStatus }}</p>
              </div>
              <div class="flex-shrink-0">
                <svg class="w-5 h-5 text-blue-500 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
                </svg>
              </div>
            </div>
          </div>

          <!-- Lista de agentes del pipeline -->
          <div class="w-full space-y-2">
            <p class="text-xs font-semibold text-gray-500 uppercase tracking-wide">Pipeline de Procesamiento ({{ completedAgents }}/7)</p>
            <div class="grid grid-cols-7 gap-2">
              <div v-for="(agent, index) in agents" :key="index" 
                   class="flex flex-col items-center space-y-1">
                <div class="w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300"
                     :class="getAgentClass(index + 1)">
                  <span v-if="index + 1 <= completedAgents" class="text-white text-xs font-bold">✓</span>
                  <span v-else class="text-xs font-medium">{{ agent.letter }}</span>
                </div>
                <span class="text-[10px] text-gray-500 text-center leading-tight">{{ agent.name }}</span>
              </div>
            </div>
          </div>

          <!-- Tiempo transcurrido -->
          <div class="text-sm text-gray-500">
            <span class="font-medium">Tiempo:</span> {{ elapsedTime }}s
          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { ref, computed, watch, onUnmounted } from 'vue'

interface Props {
  isVisible: boolean
  title?: string
  message?: string
  currentAgent?: string
  currentAgentNumber?: number
  completedAgents?: number
}

const props = withDefaults(defineProps<Props>(), {
  title: 'Procesando Reporte',
  message: 'Analizando resultados de laboratorio con IA...',
  currentAgent: '',
  currentAgentNumber: 0,
  completedAgents: 0
})

defineEmits(['close'])

const agents = [
  { letter: 'A', name: 'Extractor' },
  { letter: 'B', name: 'Validador' },
  { letter: 'C', name: 'Clínico' },
  { letter: 'D', name: 'Auditor' },
  { letter: 'E', name: 'Corrector' },
  { letter: 'F', name: 'Paciente' },
  { letter: 'G', name: 'Control' }
]

const elapsedTime = ref(0)
let intervalId: number | null = null

const agentStatus = computed(() => {
  if (props.completedAgents === 7) return 'Completado ✓'
  return 'Procesando...'
})

const getAgentClass = (step: number) => {
  if (step <= props.completedAgents) {
    // Completado - Verde
    return 'bg-green-500 text-white shadow-lg scale-110 border-2 border-green-600'
  } else if (step === props.currentAgentNumber && props.currentAgentNumber > props.completedAgents) {
    // En proceso - Azul
    return 'bg-blue-500 text-white shadow-lg animate-pulse border-2 border-blue-600'
  } else {
    // Pendiente - Gris
    return 'bg-gray-200 text-gray-600 border-2 border-gray-300'
  }
}

// Timer
watch(() => props.isVisible, (visible) => {
  if (visible) {
    elapsedTime.value = 0
    intervalId = window.setInterval(() => {
      elapsedTime.value++
    }, 1000)
  } else {
    if (intervalId) {
      clearInterval(intervalId)
      intervalId = null
    }
  }
})

onUnmounted(() => {
  if (intervalId) {
    clearInterval(intervalId)
  }
})
</script>

<style scoped>
/* Animación del modal */
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-active > div,
.modal-leave-active > div {
  transition: transform 0.3s ease;
}

.modal-enter-from > div,
.modal-leave-to > div {
  transform: scale(0.9);
}

/* Animación de la barra de progreso */
@keyframes progress {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}

.animate-progress {
  animation: progress 1.5s ease-in-out infinite;
}
</style> 